/**
 * DecisionFlow — RevTypeRateTableManager
 * data-fit LLC · Confidential
 * Domain: P3-01 Revenue-Type Differentiation (Standard tier — feature_gate_key = NULL)
 * Spec authority: decisionflow_phase3_plan_spec_v1_0.md §2
 * API Contract: api_contract_v1_32.md §48
 * Wizard substages:
 *   revenue_type_rate_definition · revenue_type_quota_mapping ·
 *   revenue_type_approval · revenue_type_validation
 *
 * What this manager does:
 *   Tab 1 — Rate Definition:
 *     Select plan_component → view rate bands grouped by revenue_type
 *     Add / edit / delete individual rate band rows per revenue_type
 *     Submit individual band rows for approval
 *   Tab 2 — Quota Mapping:
 *     Define quota allocations per revenue_type per position per fiscal_period
 *     View existing mappings for this component + period
 *   Tab 3 — Approval:
 *     List all unapproved rate_id rows for this component
 *     Approve individual rows (actor ≠ submitter)
 *   Tab 4 — Validation:
 *     Advisory check: bands complete, no gaps, quota mappings present
 *
 * DDL ground truth (phase3_plan_spec §2.2):
 *   revenue_type_rate_table:
 *     rate_id bigint PK, tenant_id uuid, plan_component_id int FK,
 *     revenue_type_code varchar(30) FK ref_revenue_type,
 *     band_sequence smallint, from_pct numeric(6,4), to_pct numeric(6,4),
 *     payout_pct numeric(6,4), is_accelerator bool,
 *     interpolation varchar(10) CHECK(linear|step),
 *     effective_date date, end_date date,
 *     approved_by uuid, approved_at timestamptz,
 *     submitted_by uuid, submitted_at timestamptz,
 *     created_by uuid, created_at timestamptz, updated_at timestamptz
 *     UNIQUE(tenant_id, plan_component_id, revenue_type_code, band_sequence, effective_date)
 *
 *   revenue_type_quota_mapping:
 *     mapping_id int PK (API envelope: {"quota_mapping": {"mapping_id": N}})
 *     plan_component_id int FK, fiscal_period_id int FK,
 *     position_id uuid FK, revenue_type_code varchar(30) FK,
 *     quota_amount numeric(18,4), quota_currency_code char(3),
 *     allocation_pct numeric(5,4) — either quota_amount OR allocation_pct, not both
 *
 *   ref_revenue_type (seed data):
 *     new_logo, expansion, renewal, upsell, churn_recovery (allow_on_import=TRUE)
 *     unclassified — sentinel, allow_on_import=FALSE, never shown in UI
 *
 * API surface (api_contract_v1_32.md §48):
 *   GET  /api/v1/ref/revenue-types                                    — load once on mount
 *   POST /api/v1/revenue-type-rate-tables                             — create band row
 *   GET  /api/v1/revenue-type-rate-tables?plan_component_id={id}      — grouped response
 *   PUT  /api/v1/revenue-type-rate-tables/{rate_id}                   — raw fetch
 *   DELETE /api/v1/revenue-type-rate-tables/{rate_id}                 — raw fetch
 *   POST /api/v1/revenue-type-rate-tables/{rate_id}/submit
 *   POST /api/v1/revenue-type-rate-tables/{rate_id}/approve
 *   POST /api/v1/revenue-type-quota-mappings
 *   GET  /api/v1/revenue-type-quota-mappings?plan_component_id={id}&fiscal_period_id={id}
 *
 * Response envelope note (confirmed S326, api_contract_v1_32 §48.3):
 *   GET list response: { plan_component_id, data: { new_logo: [...], expansion: [...], ... } }
 *   Individual rows come back flat from POST (rate_id at top level)
 *   Quota mapping POST envelope: { quota_mapping: { mapping_id: N, ... } }
 *
 * Architecture rules (ARCH-2, NN-36):
 *   - useAuth() for { user } — no token in props
 *   - apiGet / apiPost from client — PUT/DELETE via raw fetch
 *   - tenant_id never in any request body
 *   - No window.prompt / window.confirm — inline confirmation state
 *   - Approval per-row (rate_id level), not plan-level — no ApprovalPanel component
 *   - Actor separation: approved_by ≠ submitted_by checked at API; UI shows advisory
 *
 * Props:
 *   mode             — 'wizard' | 'admin'
 *   period           — { fiscal_period_id, period_code } | null
 *   wizardSteps      — array
 *   embedded         — bool  — context injection mode (spec §4.8)
 *   planId           — int   — injected plan id
 *   planComponentId  — int   — injected component id, skip selector
 *   fiscalPeriodId   — int   — injected fiscal period
 *   readOnly         — bool  — true when plan is approved/archived (wizard context)
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../auth/AuthContext.jsx';
import { apiGet, apiPost } from '../../api/client';
import GateStatusBanner from '../shared/GateStatusBanner.jsx';
import StatusPill from '../shared/StatusPill.jsx';

// ---------------------------------------------------------------------------
// Constants — DDL-authoritative
// ---------------------------------------------------------------------------

const INTERPOLATION_OPTIONS = [
  { value: 'step',   label: 'Step (flat rate within band)' },
  { value: 'linear', label: 'Linear (interpolate within band)' },
];

const TABS = [
  { key: 'rates',    label: 'Rate Definition'  },
  { key: 'quotas',   label: 'Quota Mapping'    },
  { key: 'approval', label: 'Approval'         },
  { key: 'validate', label: 'Validation'       },
];

// Revenue types that can be used in rate tables (exclude 'unclassified' sentinel)
// Loaded dynamically from /api/v1/ref/revenue-types; this is the fallback
const FALLBACK_REVENUE_TYPES = [
  { revenue_type_code: 'new_logo',       display_name: 'New Logo',       subtracts_from_payout: false },
  { revenue_type_code: 'expansion',      display_name: 'Expansion',      subtracts_from_payout: false },
  { revenue_type_code: 'renewal',        display_name: 'Renewal',        subtracts_from_payout: false },
  { revenue_type_code: 'upsell',         display_name: 'Upsell',         subtracts_from_payout: false },
  { revenue_type_code: 'churn_recovery', display_name: 'Churn Recovery', subtracts_from_payout: true  },
];

const EMPTY_BAND_FORM = {
  revenue_type_code: 'new_logo',
  band_sequence:     '1',
  from_pct:          '',
  to_pct:            '',
  payout_pct:        '',
  is_accelerator:    false,
  interpolation:     'step',
  effective_date:    '',
  end_date:          '',
};

const EMPTY_QUOTA_FORM = {
  position_id:       '',
  revenue_type_code: 'new_logo',
  quota_amount:      '',
  quota_currency_code: 'USD',
  allocation_pct:    '',
};

const CURRENCY_CODES = ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'SGD', 'JPY', 'CNY'];

// ---------------------------------------------------------------------------
// Shared CSS class strings — token-compliant
// ---------------------------------------------------------------------------

const thClass =
  'px-3 py-2 text-left text-tok-xs font-semibold uppercase tracking-wide ' +
  'text-[rgb(var(--color-text-muted))] bg-[rgb(var(--color-surface-muted))] ' +
  'border-b border-[rgb(var(--color-border))]';

const tdClass =
  'px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] ' +
  'border-b border-[rgb(var(--color-border))]';

const btnPrimary =
  'rounded-lg bg-[rgb(var(--color-primary))] px-3 py-1.5 text-tok-sm font-medium ' +
  'text-white hover:bg-[rgb(var(--color-primary-hover))] ' +
  'disabled:opacity-50 disabled:cursor-not-allowed';

const btnSecondary =
  'rounded-lg bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] ' +
  'px-3 py-1.5 text-tok-sm font-medium text-[rgb(var(--color-text))] ' +
  'hover:bg-[rgb(var(--color-surface-muted))] ' +
  'disabled:opacity-50 disabled:cursor-not-allowed';

const btnDanger =
  'rounded-lg border border-[rgb(var(--color-danger))]/40 px-3 py-1.5 text-tok-sm ' +
  'font-medium text-[rgb(var(--color-danger))] hover:bg-[rgb(var(--color-danger))]/10 ' +
  'disabled:opacity-50 disabled:cursor-not-allowed';

const inputClass =
  'mt-1 block w-full rounded-lg border border-[rgb(var(--color-border))] ' +
  'bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] ' +
  'px-3 py-2 text-tok-sm focus:outline-none ' +
  'focus:ring-2 focus:ring-[rgb(var(--color-focus-ring))]';

const labelClass = 'flex flex-col text-tok-sm font-medium text-[rgb(var(--color-text))]';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getErrorMessage(error, fallback) {
  return error?.message || error?.detail || fallback;
}

function getApiErrorCode(error) {
  return error?.code || null;
}

function deriveRowState(row) {
  if (!row) return 'draft';
  if (row.approved_by)                         return 'approved';
  if (row.submitted_at || row.submitted_by)    return 'pending_approval';
  return 'draft';
}

function fmtPct(val) {
  if (val === null || val === undefined || val === '') return '—';
  return `${(Number(val) * 100).toFixed(2)}%`;
}

function fmtDate(val) {
  if (!val) return '—';
  return String(val).slice(0, 10);
}

// Flatten grouped API response into an array of rows
// GET /api/v1/revenue-type-rate-tables returns { plan_component_id, data: { new_logo: [...], ... } }
function flattenGroupedRates(apiData) {
  if (!apiData) return [];
  const grouped = apiData?.data || apiData;
  if (Array.isArray(grouped)) return grouped; // already flat
  const rows = [];
  for (const [, bandRows] of Object.entries(grouped)) {
    if (Array.isArray(bandRows)) rows.push(...bandRows);
  }
  return rows;
}

// Group flat rows by revenue_type_code
function groupByRevenueType(rows) {
  return rows.reduce((acc, row) => {
    const key = row.revenue_type_code || 'unclassified';
    if (!acc[key]) acc[key] = [];
    acc[key].push(row);
    return acc;
  }, {});
}

// ---------------------------------------------------------------------------
// Sub-component: BandTable — rate bands for a single revenue_type
// ---------------------------------------------------------------------------

function BandTable({
  revType,
  rows,
  isDraftOnly,
  onAddRow,
  onEditRow,
  onDeleteRow,
  onSubmitRow,
  currentUserId,
}) {
  const sorted = [...rows].sort((a, b) => (a.band_sequence ?? 0) - (b.band_sequence ?? 0));

  return (
    <div className="overflow-x-auto rounded-lg border border-[rgb(var(--color-border))]">
      <table className="min-w-full text-tok-sm border-collapse">
        <thead>
          <tr>
            <th className={thClass}>#</th>
            <th className={thClass}>From %</th>
            <th className={thClass}>To %</th>
            <th className={thClass}>Payout %</th>
            <th className={thClass}>Accel.</th>
            <th className={thClass}>Interp.</th>
            <th className={thClass}>Effective</th>
            <th className={thClass}>Status</th>
            <th className={thClass} aria-label="Actions" />
          </tr>
        </thead>
        <tbody>
          {sorted.map((row) => {
            const state = deriveRowState(row);
            const isDraft = state === 'draft';
            const isPending = state === 'pending_approval';
            const isSelfSubmitter = row.submitted_by && row.submitted_by === currentUserId;
            return (
              <tr key={row.rate_id} className="hover:bg-[rgb(var(--color-surface-muted))]">
                <td className={tdClass + ' font-medium'}>{row.band_sequence}</td>
                <td className={tdClass}>{fmtPct(row.from_pct)}</td>
                <td className={tdClass}>{fmtPct(row.to_pct)}</td>
                <td className={tdClass + ' font-medium'}>{fmtPct(row.payout_pct)}</td>
                <td className={tdClass}>{row.is_accelerator ? '✓' : '—'}</td>
                <td className={tdClass}>{row.interpolation || '—'}</td>
                <td className={tdClass}>{fmtDate(row.effective_date)}</td>
                <td className={tdClass}>
                  <StatusPill status={state} />
                </td>
                <td className={tdClass}>
                  <div className="flex items-center gap-2 flex-wrap">
                    {isDraft && (
                      <>
                        <button type="button"
                          className="text-tok-sm text-[rgb(var(--color-primary))] hover:underline"
                          onClick={() => onEditRow(row)}>
                          Edit
                        </button>
                        <button type="button"
                          className="text-tok-sm text-[rgb(var(--color-primary))] hover:underline"
                          onClick={() => onSubmitRow(row.rate_id, 'submit')}>
                          Submit
                        </button>
                        <button type="button"
                          className="text-tok-sm text-[rgb(var(--color-danger))] hover:underline"
                          onClick={() => onDeleteRow(row.rate_id)}>
                          Delete
                        </button>
                      </>
                    )}
                    {isPending && !isSelfSubmitter && (
                      <button type="button"
                        className="text-tok-sm text-[rgb(var(--color-success))] hover:underline font-medium"
                        onClick={() => onSubmitRow(row.rate_id, 'approve')}>
                        Approve
                      </button>
                    )}
                    {isPending && isSelfSubmitter && (
                      <span className="text-tok-xs text-[rgb(var(--color-text-muted))] italic">
                        Awaiting another approver
                      </span>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function RevTypeRateTableManager({ mode, period, wizardSteps, embedded = false, planId, planComponentId, fiscalPeriodId, readOnly = false }) {
  const { user } = useAuth();

  const [activeTab, setActiveTab]                   = useState('rates');
  const [featureUnavailable, setFeatureUnavailable] = useState(false);
  const [toast, setToast]                           = useState(null);

  // Reference data
  const [revenueTypes, setRevenueTypes]             = useState(FALLBACK_REVENUE_TYPES);

  // Plan component selector
  const [components, setComponents]                 = useState([]);
  const [componentsLoading, setComponentsLoading]   = useState(false);
  const [componentsError, setComponentsError]       = useState('');
  const [selectedComponentId, setSelectedComponentId] = useState('');

  // Rate bands
  const [allRows, setAllRows]                       = useState([]);
  const [ratesLoading, setRatesLoading]             = useState(false);
  const [ratesError, setRatesError]                 = useState('');

  // Band form
  const [showBandForm, setShowBandForm]             = useState(false);
  const [bandForm, setBandForm]                     = useState(EMPTY_BAND_FORM);
  const [bandSaving, setBandSaving]                 = useState(false);
  const [bandFormError, setBandFormError]           = useState('');
  const [editingRateId, setEditingRateId]           = useState(null);
  const [confirmDeleteRateId, setConfirmDeleteRateId] = useState(null);
  const [rowActionLoading, setRowActionLoading]     = useState(null); // rate_id of in-flight action
  const [rowActionError, setRowActionError]         = useState('');

  // Quota mappings
  const [quotaMappings, setQuotaMappings]           = useState([]);
  const [quotasLoading, setQuotasLoading]           = useState(false);
  const [quotasError, setQuotasError]               = useState('');
  const [showQuotaForm, setShowQuotaForm]           = useState(false);
  const [quotaForm, setQuotaForm]                   = useState(EMPTY_QUOTA_FORM);
  const [quotaSaving, setQuotaSaving]               = useState(false);
  const [quotaFormError, setQuotaFormError]         = useState('');

  // Positions (for quota mapping selector)
  const [positions, setPositions]                   = useState([]);

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  const showToast = useCallback((message) => {
    setToast(message);
    window.setTimeout(() => setToast(null), 3000);
  }, []);

  const currentUserId = user?.user_id || user?.id || null;

  // Active revenue types (exclude sentinel 'unclassified')
  const activeRevenueTypes = useMemo(
    () => revenueTypes.filter((rt) => rt.revenue_type_code !== 'unclassified'),
    [revenueTypes]
  );

  // ---------------------------------------------------------------------------
  // Boot: reference data + components
  // ---------------------------------------------------------------------------

  const loadRefData = useCallback(async () => {
    try {
      const data = await apiGet('/api/v1/ref/revenue-types');
      const types = Array.isArray(data) ? data : data?.data || [];
      const active = types.filter((t) => t.revenue_type_code !== 'unclassified' && t.is_active !== false);
      if (active.length > 0) setRevenueTypes(active);
    } catch { /* use fallback */ }
  }, []);

  const loadComponents = useCallback(async () => {
    if (!user) return;
    setComponentsLoading(true);
    setComponentsError('');
    try {
      // C7 — S689 plan_id scoping: scope component list to this plan when planId is injected.
      // Without this, managers show components from all plans on the tenant (AcceleratorCap pattern).
      const periodId = period?.fiscal_period_id;
      const params = new URLSearchParams();
      if (planId != null) params.set('plan_id', String(planId));
      if (periodId != null) params.set('fiscal_period_id', String(periodId));
      const url = `/api/v1/plan-components${params.toString() ? '?' + params.toString() : ''}`;
      const data = await apiGet(url);
      setComponents(Array.isArray(data) ? data : data?.data || data?.plan_components || []);
    } catch (error) {
      if (getApiErrorCode(error) === 'FEATURE_UNAVAILABLE' || error?.status === 403) {
        setFeatureUnavailable(true);
        return;
      }
      setComponentsError(getErrorMessage(error, 'Unable to load plan components.'));
    } finally {
      setComponentsLoading(false);
    }
  }, [user, period]);

  const loadPositions = useCallback(async () => {
    if (!user) return;
    try {
      const data = await apiGet('/api/v1/positions');
      setPositions(Array.isArray(data) ? data : data?.positions || data?.data || []);
    } catch { /* non-blocking */ }
  }, [user]);

  useEffect(() => {
    loadRefData();
    loadComponents();
    loadPositions();
  }, [loadRefData, loadComponents, loadPositions]);

  // Embedded context injection (spec §4.8)
  useEffect(() => {
    if (embedded && planComponentId != null) {
      setSelectedComponentId(String(planComponentId));
    }
  }, [embedded, planComponentId]);

  // ---------------------------------------------------------------------------
  // Load rate bands for selected component
  // ---------------------------------------------------------------------------

  const loadRates = useCallback(async (componentId) => {
    if (!componentId) return;
    setRatesLoading(true);
    setRatesError('');
    setAllRows([]);
    try {
      const data = await apiGet(`/api/v1/revenue-type-rate-tables?plan_component_id=${componentId}`);
      setAllRows(flattenGroupedRates(data));
    } catch (error) {
      setRatesError(getErrorMessage(error, 'Unable to load rate bands.'));
    } finally {
      setRatesLoading(false);
    }
  }, []);

  // ---------------------------------------------------------------------------
  // Load quota mappings for selected component + period
  // ---------------------------------------------------------------------------

  const loadQuotaMappings = useCallback(async (componentId) => {
    if (!componentId) return;
    setQuotasLoading(true);
    setQuotasError('');
    try {
      const periodId = period?.fiscal_period_id;
      const url = periodId
        ? `/api/v1/revenue-type-quota-mappings?plan_component_id=${componentId}&fiscal_period_id=${periodId}`
        : `/api/v1/revenue-type-quota-mappings?plan_component_id=${componentId}`;
      const data = await apiGet(url);
      setQuotaMappings(Array.isArray(data) ? data : data?.data || []);
    } catch (error) {
      setQuotasError(getErrorMessage(error, 'Unable to load quota mappings.'));
    } finally {
      setQuotasLoading(false);
    }
  }, [period]);

  useEffect(() => {
    if (selectedComponentId) {
      loadRates(selectedComponentId);
      loadQuotaMappings(selectedComponentId);
    }
  }, [selectedComponentId, loadRates, loadQuotaMappings]);

  // ---------------------------------------------------------------------------
  // Derived
  // ---------------------------------------------------------------------------

  const grouped = useMemo(() => groupByRevenueType(allRows), [allRows]);

  const unapprovedRows = useMemo(
    () => allRows.filter((r) => !r.approved_by),
    [allRows]
  );

  const nextBandSequence = useCallback((revTypeCode) => {
    const rows = grouped[revTypeCode] || [];
    return rows.length > 0 ? Math.max(...rows.map((r) => r.band_sequence ?? 0)) + 1 : 1;
  }, [grouped]);

  const positionNameById = useMemo(
    () => positions.reduce((acc, p) => {
      acc[String(p.position_id)] = p.full_name || `Position ${p.position_id}`;
      return acc;
    }, {}),
    [positions]
  );

  // ---------------------------------------------------------------------------
  // Band form helpers
  // ---------------------------------------------------------------------------

  const updateBandForm = useCallback((field, value) =>
    setBandForm((prev) => ({ ...prev, [field]: value })), []);

  const resetBandForm = useCallback(() => {
    setBandForm(EMPTY_BAND_FORM);
    setBandFormError('');
    setEditingRateId(null);
  }, []);

  const validateBandForm = useCallback(() => {
    if (!bandForm.revenue_type_code)          return 'Revenue type is required.';
    const seq = Number(bandForm.band_sequence);
    if (!Number.isInteger(seq) || seq < 1)    return 'Band sequence must be a positive integer.';
    const from = Number(bandForm.from_pct);
    const to   = Number(bandForm.to_pct);
    if (isNaN(from) || from < 0)              return 'From % must be a non-negative number.';
    if (isNaN(to) || to <= from)              return 'To % must be greater than From %.';
    if (isNaN(Number(bandForm.payout_pct)) || Number(bandForm.payout_pct) < 0)
      return 'Payout % must be a non-negative number.';
    if (!bandForm.interpolation)              return 'Interpolation method is required.';
    if (!bandForm.effective_date)             return 'Effective date is required.';
    return '';
  }, [bandForm]);

  // ---------------------------------------------------------------------------
  // Create band row
  // ---------------------------------------------------------------------------

  const createBand = useCallback(async () => {
    if (!selectedComponentId) { setBandFormError('Select a plan component first.'); return; }
    const err = validateBandForm();
    if (err) { setBandFormError(err); return; }
    setBandSaving(true);
    setBandFormError('');
    try {
      const payload = {
        plan_component_id: Number(selectedComponentId),
        revenue_type_code: bandForm.revenue_type_code,
        band_sequence:     Number(bandForm.band_sequence),
        from_pct:          bandForm.from_pct,
        to_pct:            bandForm.to_pct,
        payout_pct:        bandForm.payout_pct,
        is_accelerator:    bandForm.is_accelerator,
        interpolation:     bandForm.interpolation,
        effective_date:    bandForm.effective_date,
        end_date:          bandForm.end_date || null,
      };
      await apiPost('/api/v1/revenue-type-rate-tables', payload);
      await loadRates(selectedComponentId);
      setShowBandForm(false);
      resetBandForm();
      showToast('Rate band created.');
    } catch (error) {
      const code = getApiErrorCode(error);
      if (code === 'RATE_BAND_GAP')     setBandFormError('Band gap detected. Bands must be contiguous — check your From/To percentages.');
      else if (code === 'RATE_BAND_OVERLAP') setBandFormError('Band overlap detected. Adjust From/To percentages so bands do not overlap.');
      else setBandFormError(getErrorMessage(error, 'Unable to create rate band.'));
    } finally {
      setBandSaving(false);
    }
  }, [bandForm, loadRates, resetBandForm, selectedComponentId, showToast, validateBandForm]);

  // ---------------------------------------------------------------------------
  // Update band row (PUT — raw fetch)
  // ---------------------------------------------------------------------------

  const updateBand = useCallback(async () => {
    if (!editingRateId) return;
    const err = validateBandForm();
    if (err) { setBandFormError(err); return; }
    setBandSaving(true);
    setBandFormError('');
    try {
      const res = await fetch(`/api/v1/revenue-type-rate-tables/${editingRateId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          band_sequence:  Number(bandForm.band_sequence),
          from_pct:       bandForm.from_pct,
          to_pct:         bandForm.to_pct,
          payout_pct:     bandForm.payout_pct,
          is_accelerator: bandForm.is_accelerator,
          interpolation:  bandForm.interpolation,
          effective_date: bandForm.effective_date,
          end_date:       bandForm.end_date || null,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const code = body?.error?.code || body?.code;
        if (code === 'CONFIG_ALREADY_APPROVED')
          throw new Error('This band row is already approved and cannot be edited.');
        if (code === 'RATE_BAND_GAP')
          throw new Error('Band gap detected after update. Adjust From/To values to keep bands contiguous.');
        if (code === 'RATE_BAND_OVERLAP')
          throw new Error('Band overlap detected after update.');
        throw new Error(body?.error?.message || body?.detail || `Update failed (${res.status})`);
      }
      await loadRates(selectedComponentId);
      setShowBandForm(false);
      resetBandForm();
      showToast('Rate band updated.');
    } catch (error) {
      setBandFormError(error.message || 'Unable to update rate band.');
    } finally {
      setBandSaving(false);
    }
  }, [bandForm, editingRateId, loadRates, resetBandForm, selectedComponentId, showToast, validateBandForm]);

  const openEditBand = useCallback((row) => {
    setEditingRateId(row.rate_id);
    setBandForm({
      revenue_type_code: row.revenue_type_code,
      band_sequence:     String(row.band_sequence ?? '1'),
      from_pct:          row.from_pct != null ? String(row.from_pct) : '',
      to_pct:            row.to_pct != null ? String(row.to_pct) : '',
      payout_pct:        row.payout_pct != null ? String(row.payout_pct) : '',
      is_accelerator:    !!row.is_accelerator,
      interpolation:     row.interpolation || 'step',
      effective_date:    fmtDate(row.effective_date) === '—' ? '' : fmtDate(row.effective_date),
      end_date:          row.end_date ? fmtDate(row.end_date) : '',
    });
    setBandFormError('');
    setShowBandForm(true);
  }, []);

  // ---------------------------------------------------------------------------
  // Delete band row (DELETE — raw fetch)
  // ---------------------------------------------------------------------------

  const deleteBand = useCallback(async (rateId) => {
    setRowActionLoading(rateId);
    setRowActionError('');
    try {
      const res = await fetch(`/api/v1/revenue-type-rate-tables/${rateId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const code = body?.error?.code || body?.code;
        if (code === 'CONFIG_ALREADY_APPROVED')
          throw new Error('Approved band rows cannot be deleted.');
        throw new Error(body?.error?.message || body?.detail || `Delete failed (${res.status})`);
      }
      setConfirmDeleteRateId(null);
      await loadRates(selectedComponentId);
      showToast('Rate band deleted.');
    } catch (error) {
      setRowActionError(error.message || 'Unable to delete rate band.');
    } finally {
      setRowActionLoading(null);
    }
  }, [loadRates, selectedComponentId, showToast]);

  // ---------------------------------------------------------------------------
  // Submit / Approve band row
  // ---------------------------------------------------------------------------

  const rowAction = useCallback(async (rateId, action) => {
    setRowActionLoading(rateId);
    setRowActionError('');
    try {
      await apiPost(`/api/v1/revenue-type-rate-tables/${rateId}/${action}`, {});
      await loadRates(selectedComponentId);
      showToast(action === 'submit' ? 'Band row submitted for approval.' : 'Band row approved.');
    } catch (error) {
      const code = getApiErrorCode(error);
      if (code === 'SELF_APPROVAL_BLOCKED')
        setRowActionError('You submitted this row — a different user must approve it.');
      else
        setRowActionError(getErrorMessage(error, `Unable to ${action} band row.`));
    } finally {
      setRowActionLoading(null);
    }
  }, [loadRates, selectedComponentId, showToast]);

  // ---------------------------------------------------------------------------
  // Quota mapping form
  // ---------------------------------------------------------------------------

  const updateQuotaForm = useCallback((field, value) =>
    setQuotaForm((prev) => ({ ...prev, [field]: value })), []);

  const validateQuotaForm = useCallback(() => {
    if (!quotaForm.position_id)       return 'Position is required.';
    if (!quotaForm.revenue_type_code) return 'Revenue type is required.';
    const hasAmount = quotaForm.quota_amount !== '' && quotaForm.quota_amount != null;
    const hasPct    = quotaForm.allocation_pct !== '' && quotaForm.allocation_pct != null;
    if (hasAmount && hasPct)          return 'Provide either a quota amount or an allocation percentage — not both.';
    if (!hasAmount && !hasPct)        return 'Either a quota amount or an allocation percentage is required.';
    if (hasAmount) {
      if (isNaN(Number(quotaForm.quota_amount)) || Number(quotaForm.quota_amount) <= 0)
        return 'Quota amount must be a positive number.';
      if (!quotaForm.quota_currency_code) return 'Currency code is required with a quota amount.';
    }
    if (hasPct) {
      const pct = Number(quotaForm.allocation_pct);
      if (isNaN(pct) || pct <= 0 || pct > 1) return 'Allocation % must be between 0.0001 and 1.0000.';
    }
    return '';
  }, [quotaForm]);

  const createQuotaMapping = useCallback(async () => {
    if (!selectedComponentId) { setQuotaFormError('Select a plan component first.'); return; }
    if (!period?.fiscal_period_id) { setQuotaFormError('A fiscal period is required for quota mappings.'); return; }
    const err = validateQuotaForm();
    if (err) { setQuotaFormError(err); return; }
    setQuotaSaving(true);
    setQuotaFormError('');
    try {
      const hasAmount = quotaForm.quota_amount !== '' && quotaForm.quota_amount != null;
      const payload = {
        plan_component_id:   Number(selectedComponentId),
        fiscal_period_id:    Number(period.fiscal_period_id),
        position_id:         quotaForm.position_id,
        revenue_type_code:   quotaForm.revenue_type_code,
        quota_amount:        hasAmount ? quotaForm.quota_amount : null,
        quota_currency_code: hasAmount ? quotaForm.quota_currency_code : null,
        allocation_pct:      !hasAmount ? quotaForm.allocation_pct : null,
      };
      await apiPost('/api/v1/revenue-type-quota-mappings', payload);
      await loadQuotaMappings(selectedComponentId);
      setShowQuotaForm(false);
      setQuotaForm(EMPTY_QUOTA_FORM);
      showToast('Quota mapping created.');
    } catch (error) {
      setQuotaFormError(getErrorMessage(error, 'Unable to create quota mapping.'));
    } finally {
      setQuotaSaving(false);
    }
  }, [loadQuotaMappings, period, quotaForm, selectedComponentId, showToast, validateQuotaForm]);

  // ---------------------------------------------------------------------------
  // Validation tab
  // ---------------------------------------------------------------------------

  const validationSummary = useMemo(() => {
    if (!selectedComponentId || allRows.length === 0) return null;
    const issues = [];
    for (const rt of activeRevenueTypes) {
      const rows = grouped[rt.revenue_type_code] || [];
      if (rows.length === 0) continue; // no rows for this type = not configured (not an error)
      // Check all approved
      const unapproved = rows.filter((r) => !r.approved_by);
      if (unapproved.length > 0)
        issues.push({ type: 'unapproved', revType: rt.display_name, count: unapproved.length });
      // Check band continuity (from_pct of next band should equal to_pct of previous)
      const sorted = [...rows].sort((a, b) => (a.band_sequence ?? 0) - (b.band_sequence ?? 0));
      for (let i = 1; i < sorted.length; i++) {
        if (Number(sorted[i].from_pct) !== Number(sorted[i - 1].to_pct)) {
          issues.push({ type: 'gap', revType: rt.display_name,
            detail: `Gap between band ${sorted[i - 1].band_sequence} and ${sorted[i].band_sequence}` });
        }
      }
    }
    const configuredTypes = Object.keys(grouped).filter((k) => k !== 'unclassified');
    const quotaMappedTypes = [...new Set(quotaMappings.map((m) => m.revenue_type_code))];
    const unmappedTypes = configuredTypes.filter((t) => !quotaMappedTypes.includes(t));
    if (unmappedTypes.length > 0)
      issues.push({ type: 'no_quota', detail: `No quota mapping for: ${unmappedTypes.join(', ')}` });
    return { configured: configuredTypes.length, issues };
  }, [activeRevenueTypes, allRows, grouped, quotaMappings, selectedComponentId]);

  // ---------------------------------------------------------------------------
  // Feature gate / auth guard
  // ---------------------------------------------------------------------------

  if (featureUnavailable) return <GateStatusBanner feature="Revenue-Type Differentiation" />;

  if (!user) {
    return (
      <section className="flex flex-col gap-6">
        <h2 className="text-tok-xl font-semibold text-[rgb(var(--color-text))]">Revenue-Type Rates</h2>
        <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
          Authentication required.
        </div>
      </section>
    );
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <section className="flex flex-col gap-6">
      {toast && (
        <div role="status" className="fixed bottom-4 right-4 z-50 rounded-lg bg-[rgb(var(--color-primary))] px-4 py-3 text-tok-sm text-white shadow-lg">
          {toast}
        </div>
      )}

      <header>
        {mode === 'wizard' && (
          <p className="text-tok-xs font-semibold uppercase tracking-widest text-[rgb(var(--color-text-muted))] mb-1">
            Stage 8 · P3-01
          </p>
        )}
        <h2 className="text-tok-xl font-semibold text-[rgb(var(--color-text))]">Revenue-Type Differentiation</h2>
        <p className="mt-1 text-tok-sm text-[rgb(var(--color-text-muted))]">
          Configure separate commission rate bands for each revenue type — new logo, expansion, renewal,
          upsell, and churn recovery. Each type accumulates attainment independently.
        </p>
      </header>

      {!embedded && (
      <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-4 flex items-end gap-3 flex-wrap shadow-sm">
        {/* Component selector — persistent across all tabs */}
        <label className={labelClass} style={{ flex: '1', minWidth: '240px' }}>
          Plan component
          {componentsLoading && <span className="ml-2 text-tok-xs text-[rgb(var(--color-text-muted))]">Loading…</span>}
          <select
            className={inputClass}
            value={selectedComponentId}
            onChange={(e) => {
              setSelectedComponentId(e.target.value);
              setAllRows([]);
              setQuotaMappings([]);
              setShowBandForm(false);
              resetBandForm();
              setShowQuotaForm(false);
            }}
          >
            <option value="">Select a plan component…</option>
            {components.map((c) => (
              <option key={c.plan_component_id} value={c.plan_component_id}>
                {c.role_code || c.role_id || `Component ${c.plan_component_id}`}
                {c.segment_code ? ` — ${c.segment_code}` : ''}
              </option>
            ))}
          </select>
        </label>
        {selectedComponentId && (
          <div className="flex gap-2 text-tok-xs text-[rgb(var(--color-text-muted))]">
            <span className="rounded-full bg-[rgb(var(--color-surface-muted))] border border-[rgb(var(--color-border))] px-3 py-1">
              {allRows.length} band{allRows.length !== 1 ? 's' : ''}
            </span>
            <span className="rounded-full bg-[rgb(var(--color-surface-muted))] border border-[rgb(var(--color-border))] px-3 py-1">
              {unapprovedRows.length} pending approval
            </span>
          </div>
        )}
      </div>
      )}

      {componentsError && (
        <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
          {componentsError}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-[rgb(var(--color-border))]">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            className={[
              'px-4 py-2 text-tok-sm font-medium border-b-2 -mb-px transition-colors',
              activeTab === tab.key
                ? 'border-[rgb(var(--color-primary))] text-[rgb(var(--color-primary))]'
                : 'border-transparent text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))]',
            ].join(' ')}
          >
            {tab.label}
            {tab.key === 'approval' && unapprovedRows.length > 0 && (
              <span className="ml-1.5 inline-block rounded-full bg-[rgb(var(--color-warning))]/20 text-[rgb(var(--color-warning))] text-tok-xs px-1.5 py-0.5">
                {unapprovedRows.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── Rate Definition tab ── */}
      {activeTab === 'rates' && (
        <div className="flex flex-col gap-6">
          {!selectedComponentId && (
            <div className="rounded-lg bg-[rgb(var(--color-surface-muted))] border border-[rgb(var(--color-border))] px-5 py-8 text-center">
              <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
                Select a plan component above to view and configure revenue-type rate bands.
              </p>
            </div>
          )}

          {selectedComponentId && (
            <>
              {/* Add band button + form */}
              <div className="flex items-center justify-between gap-2">
                <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
                  Rate bands are grouped by revenue type. Each type must have contiguous,
                  non-overlapping bands covering all relevant attainment ranges.
                </p>
                {!showBandForm && (
                  <button type="button" className={btnPrimary}
                    onClick={() => {
                      resetBandForm();
                      setBandForm((prev) => ({ ...prev, band_sequence: String(nextBandSequence(prev.revenue_type_code)) }));
                      setShowBandForm(true);
                    }}>
                    + Add band
                  </button>
                )}
              </div>

              {/* Band form */}
              {showBandForm && (
                <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 flex flex-col gap-4 shadow-sm">
                  <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">
                    {editingRateId ? 'Edit rate band' : 'New rate band'}
                  </h3>
                  {bandFormError && (
                    <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
                      {bandFormError}
                    </div>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <label className={labelClass}>
                      Revenue type <span className="text-[rgb(var(--color-danger))]">*</span>
                      <select className={inputClass} value={bandForm.revenue_type_code}
                        disabled={!!editingRateId}
                        onChange={(e) => {
                          updateBandForm('revenue_type_code', e.target.value);
                          setBandForm((prev) => ({ ...prev, revenue_type_code: e.target.value,
                            band_sequence: String(nextBandSequence(e.target.value)) }));
                        }}>
                        {activeRevenueTypes.map((rt) => (
                          <option key={rt.revenue_type_code} value={rt.revenue_type_code}>
                            {rt.display_name}
                            {rt.subtracts_from_payout ? ' (subtracts)' : ''}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className={labelClass}>
                      Band sequence <span className="text-[rgb(var(--color-danger))]">*</span>
                      <input type="number" min="1" step="1" className={inputClass}
                        value={bandForm.band_sequence}
                        onChange={(e) => updateBandForm('band_sequence', e.target.value)} />
                    </label>
                    <label className={labelClass}>
                      Interpolation <span className="text-[rgb(var(--color-danger))]">*</span>
                      <select className={inputClass} value={bandForm.interpolation}
                        onChange={(e) => updateBandForm('interpolation', e.target.value)}>
                        {INTERPOLATION_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </label>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <label className={labelClass}>
                      From % <span className="text-[rgb(var(--color-danger))]">*</span>
                      <input type="number" step="0.0001" min="0" className={inputClass}
                        value={bandForm.from_pct}
                        onChange={(e) => updateBandForm('from_pct', e.target.value)}
                        placeholder="e.g. 0.0000" />
                      <span className="mt-1 text-tok-xs text-[rgb(var(--color-text-muted))]">Decimal — 0.0 = 0%, 1.0 = 100%</span>
                    </label>
                    <label className={labelClass}>
                      To % <span className="text-[rgb(var(--color-danger))]">*</span>
                      <input type="number" step="0.0001" min="0" className={inputClass}
                        value={bandForm.to_pct}
                        onChange={(e) => updateBandForm('to_pct', e.target.value)}
                        placeholder="e.g. 1.0000" />
                    </label>
                    <label className={labelClass}>
                      Payout % <span className="text-[rgb(var(--color-danger))]">*</span>
                      <input type="number" step="0.0001" min="0" className={inputClass}
                        value={bandForm.payout_pct}
                        onChange={(e) => updateBandForm('payout_pct', e.target.value)}
                        placeholder="e.g. 0.0800 for 8%" />
                    </label>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <label className={labelClass}>
                      Effective date <span className="text-[rgb(var(--color-danger))]">*</span>
                      <input type="date" className={inputClass}
                        value={bandForm.effective_date}
                        onChange={(e) => updateBandForm('effective_date', e.target.value)} />
                    </label>
                    <label className={labelClass}>
                      End date
                      <input type="date" className={inputClass}
                        value={bandForm.end_date}
                        onChange={(e) => updateBandForm('end_date', e.target.value)} />
                    </label>
                    <label className={labelClass + ' flex-row items-center gap-2 justify-start'} style={{ marginTop: '1.5rem' }}>
                      <input type="checkbox" className="rounded border-[rgb(var(--color-border))]"
                        checked={bandForm.is_accelerator}
                        onChange={(e) => updateBandForm('is_accelerator', e.target.checked)} />
                      <span>Accelerator band</span>
                    </label>
                  </div>
                  <div className="flex items-center gap-2">
                    <button type="button" className={btnPrimary}
                      onClick={editingRateId ? updateBand : createBand}
                      disabled={bandSaving}>
                      {bandSaving ? 'Saving…' : editingRateId ? 'Save band' : 'Create band'}
                    </button>
                    <button type="button" className={btnSecondary}
                      onClick={() => { setShowBandForm(false); resetBandForm(); }}
                      disabled={bandSaving}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {rowActionError && (
                <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
                  {rowActionError}
                </div>
              )}

              {ratesLoading && (
                <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Loading rate bands…</p>
              )}
              {ratesError && (
                <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
                  {ratesError}
                </div>
              )}

              {/* Bands grouped by revenue type */}
              {!ratesLoading && !ratesError && (
                <>
                  {allRows.length === 0 && (
                    <div className="rounded-lg bg-[rgb(var(--color-surface-muted))] border border-[rgb(var(--color-border))] px-5 py-8 text-center">
                      <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
                        No rate bands yet. Add bands for each revenue type you want to differentiate.
                        A plan component with no revenue-type rate table will use the standard ARR rate table.
                      </p>
                    </div>
                  )}
                  {activeRevenueTypes.map((rt) => {
                    const rows = grouped[rt.revenue_type_code] || [];
                    if (rows.length === 0) return null;
                    return (
                      <div key={rt.revenue_type_code} className="flex flex-col gap-2">
                        <div className="flex items-center gap-2">
                          <h3 className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">
                            {rt.display_name}
                          </h3>
                          {rt.subtracts_from_payout && (
                            <span className="inline-block px-2 py-0.5 rounded-full text-tok-xs font-medium bg-[rgb(var(--color-warning))]/10 text-[rgb(var(--color-warning))]">
                              Subtracts from payout
                            </span>
                          )}
                          <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                            {rows.length} band{rows.length !== 1 ? 's' : ''}
                          </span>
                          {/* Delete confirmation for band */}
                          {confirmDeleteRateId && rows.some((r) => r.rate_id === confirmDeleteRateId) && (
                            <span className="flex items-center gap-1 ml-2 text-tok-xs">
                              <span className="text-[rgb(var(--color-text-muted))]">Delete band?</span>
                              <button type="button" className={btnDanger + ' !px-2 !py-0.5 !text-tok-xs'}
                                disabled={rowActionLoading === confirmDeleteRateId}
                                onClick={() => deleteBand(confirmDeleteRateId)}>
                                {rowActionLoading === confirmDeleteRateId ? '…' : 'Confirm'}
                              </button>
                              <button type="button" className={btnSecondary + ' !px-2 !py-0.5 !text-tok-xs'}
                                onClick={() => setConfirmDeleteRateId(null)}>
                                Cancel
                              </button>
                            </span>
                          )}
                        </div>
                        <BandTable
                          revType={rt}
                          rows={rows}
                          isDraftOnly={false}
                          onAddRow={() => {
                            resetBandForm();
                            setBandForm((prev) => ({
                              ...prev,
                              revenue_type_code: rt.revenue_type_code,
                              band_sequence: String(nextBandSequence(rt.revenue_type_code)),
                            }));
                            setShowBandForm(true);
                          }}
                          onEditRow={openEditBand}
                          onDeleteRow={(rateId) => setConfirmDeleteRateId(rateId)}
                          onSubmitRow={rowAction}
                          currentUserId={currentUserId}
                        />
                      </div>
                    );
                  })}
                </>
              )}
            </>
          )}
        </div>
      )}

      {/* ── Quota Mapping tab ── */}
      {activeTab === 'quotas' && (
        <div className="flex flex-col gap-4">
          {!selectedComponentId && (
            <div className="rounded-lg bg-[rgb(var(--color-surface-muted))] border border-[rgb(var(--color-border))] px-5 py-8 text-center">
              <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
                Select a plan component above to manage quota allocations.
              </p>
            </div>
          )}

          {selectedComponentId && (
            <>
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div>
                  <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
                    Allocate quota per revenue type per position for{' '}
                    {period?.period_code || 'the selected period'}.
                    Each bucket needs either an explicit quota amount or an allocation percentage of the rep's total quota.
                  </p>
                  {!period?.fiscal_period_id && (
                    <p className="mt-1 text-tok-xs text-[rgb(var(--color-warning))] font-medium">
                      A fiscal period is required to create quota mappings. Ensure period context is set.
                    </p>
                  )}
                </div>
                {!showQuotaForm && (
                  <button type="button" className={btnPrimary}
                    onClick={() => { setQuotaForm(EMPTY_QUOTA_FORM); setQuotaFormError(''); setShowQuotaForm(true); }}>
                    + Add mapping
                  </button>
                )}
              </div>

              {/* Quota form */}
              {showQuotaForm && (
                <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 flex flex-col gap-4 shadow-sm">
                  <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">New quota mapping</h3>
                  {quotaFormError && (
                    <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
                      {quotaFormError}
                    </div>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <label className={labelClass}>
                      Position <span className="text-[rgb(var(--color-danger))]">*</span>
                      <select className={inputClass} value={quotaForm.position_id}
                        onChange={(e) => updateQuotaForm('position_id', e.target.value)}>
                        <option value="">Select a position…</option>
                        {positions.map((p) => (
                          <option key={p.position_id} value={p.position_id}>
                            {p.full_name || p.position_id}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className={labelClass}>
                      Revenue type <span className="text-[rgb(var(--color-danger))]">*</span>
                      <select className={inputClass} value={quotaForm.revenue_type_code}
                        onChange={(e) => updateQuotaForm('revenue_type_code', e.target.value)}>
                        {activeRevenueTypes.map((rt) => (
                          <option key={rt.revenue_type_code} value={rt.revenue_type_code}>{rt.display_name}</option>
                        ))}
                      </select>
                    </label>
                  </div>
                  <p className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">
                    Quota method — provide exactly one:
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <label className={labelClass + ' sm:col-span-2'}>
                      Quota amount (absolute)
                      <div className="flex gap-2">
                        <input type="number" min="0.01" step="0.01" className={inputClass}
                          value={quotaForm.quota_amount}
                          onChange={(e) => { updateQuotaForm('quota_amount', e.target.value); if (e.target.value) updateQuotaForm('allocation_pct', ''); }}
                          placeholder="e.g. 80000.00 — leave blank if using %" />
                        <select className={inputClass + ' !mt-0'} style={{ maxWidth: '100px' }}
                          value={quotaForm.quota_currency_code}
                          onChange={(e) => updateQuotaForm('quota_currency_code', e.target.value)}>
                          {CURRENCY_CODES.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                    </label>
                    <label className={labelClass}>
                      Allocation % (of total quota)
                      <input type="number" min="0.0001" max="1" step="0.0001" className={inputClass}
                        value={quotaForm.allocation_pct}
                        onChange={(e) => { updateQuotaForm('allocation_pct', e.target.value); if (e.target.value) updateQuotaForm('quota_amount', ''); }}
                        placeholder="e.g. 0.4000 for 40%" />
                    </label>
                  </div>
                  <div className="flex items-center gap-2">
                    <button type="button" className={btnPrimary} onClick={createQuotaMapping} disabled={quotaSaving}>
                      {quotaSaving ? 'Saving…' : 'Create mapping'}
                    </button>
                    <button type="button" className={btnSecondary}
                      onClick={() => { setShowQuotaForm(false); setQuotaForm(EMPTY_QUOTA_FORM); setQuotaFormError(''); }}
                      disabled={quotaSaving}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Quota mappings table */}
              {quotasLoading && <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Loading quota mappings…</p>}
              {quotasError && (
                <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
                  {quotasError}
                </div>
              )}
              {!quotasLoading && !quotasError && quotaMappings.length === 0 && (
                <div className="rounded-lg bg-[rgb(var(--color-surface-muted))] border border-[rgb(var(--color-border))] px-5 py-6 text-center">
                  <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
                    No quota mappings yet{period?.period_code ? ` for ${period.period_code}` : ''}.
                    The engine will use proportional allocation by default when no explicit mapping exists.
                  </p>
                </div>
              )}
              {quotaMappings.length > 0 && (
                <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] shadow-sm overflow-x-auto">
                  <table className="min-w-full text-tok-sm border-collapse">
                    <thead>
                      <tr>
                        <th className={thClass}>Position</th>
                        <th className={thClass}>Revenue type</th>
                        <th className={thClass}>Quota amount</th>
                        <th className={thClass}>Currency</th>
                        <th className={thClass}>Allocation %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {quotaMappings.map((m, i) => (
                        <tr key={m.mapping_id || i} className="hover:bg-[rgb(var(--color-surface-muted))]">
                          <td className={tdClass}>{positionNameById[String(m.position_id)] || m.position_id}</td>
                          <td className={tdClass}>
                            {activeRevenueTypes.find((rt) => rt.revenue_type_code === m.revenue_type_code)?.display_name || m.revenue_type_code}
                          </td>
                          <td className={tdClass}>
                            {m.quota_amount != null
                              ? Number(m.quota_amount).toLocaleString(undefined, { minimumFractionDigits: 2 })
                              : '—'}
                          </td>
                          <td className={tdClass}>{m.quota_currency_code || '—'}</td>
                          <td className={tdClass}>
                            {m.allocation_pct != null ? fmtPct(m.allocation_pct) : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── Approval tab ── */}
      {activeTab === 'approval' && (
        <div className="flex flex-col gap-4">
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
            Approve rate band rows that have been submitted. Approval requires a different user from the
            submitter (actor separation — NN-10). Approved rows are locked and cannot be edited.
          </p>

          {!selectedComponentId && (
            <div className="rounded-lg bg-[rgb(var(--color-surface-muted))] border border-[rgb(var(--color-border))] px-5 py-8 text-center">
              <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Select a plan component above.</p>
            </div>
          )}

          {rowActionError && (
            <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
              {rowActionError}
            </div>
          )}

          {selectedComponentId && unapprovedRows.length === 0 && !ratesLoading && (
            <div className="rounded-lg bg-[rgb(var(--color-success))]/10 border border-[rgb(var(--color-success))]/30 px-5 py-6">
              <p className="text-tok-sm font-medium text-[rgb(var(--color-success))]">
                {allRows.length === 0
                  ? 'No rate bands configured for this component.'
                  : 'All rate bands are approved.'}
              </p>
            </div>
          )}

          {selectedComponentId && unapprovedRows.length > 0 && (
            <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] shadow-sm overflow-x-auto">
              <table className="min-w-full text-tok-sm border-collapse">
                <thead>
                  <tr>
                    <th className={thClass}>Revenue type</th>
                    <th className={thClass}>#</th>
                    <th className={thClass}>From %</th>
                    <th className={thClass}>To %</th>
                    <th className={thClass}>Payout %</th>
                    <th className={thClass}>Status</th>
                    <th className={thClass}>Submitted by</th>
                    <th className={thClass} aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {unapprovedRows.map((row) => {
                    const state = deriveRowState(row);
                    const isSelf = row.submitted_by === currentUserId;
                    const rt = activeRevenueTypes.find((r) => r.revenue_type_code === row.revenue_type_code);
                    return (
                      <tr key={row.rate_id} className="hover:bg-[rgb(var(--color-surface-muted))]">
                        <td className={tdClass + ' font-medium'}>{rt?.display_name || row.revenue_type_code}</td>
                        <td className={tdClass}>{row.band_sequence}</td>
                        <td className={tdClass}>{fmtPct(row.from_pct)}</td>
                        <td className={tdClass}>{fmtPct(row.to_pct)}</td>
                        <td className={tdClass}>{fmtPct(row.payout_pct)}</td>
                        <td className={tdClass}><StatusPill status={state} /></td>
                        <td className={tdClass + ' text-tok-xs text-[rgb(var(--color-text-muted))]'}>
                          {isSelf ? 'You' : (row.submitted_by ? row.submitted_by.slice(0, 8) + '…' : '—')}
                        </td>
                        <td className={tdClass}>
                          {state === 'draft' && (
                            <button type="button"
                              className="text-tok-sm text-[rgb(var(--color-primary))] hover:underline"
                              disabled={rowActionLoading === row.rate_id}
                              onClick={() => rowAction(row.rate_id, 'submit')}>
                              {rowActionLoading === row.rate_id ? '…' : 'Submit'}
                            </button>
                          )}
                          {state === 'pending_approval' && !isSelf && (
                            <button type="button"
                              className="text-tok-sm text-[rgb(var(--color-success))] hover:underline font-medium"
                              disabled={rowActionLoading === row.rate_id}
                              onClick={() => rowAction(row.rate_id, 'approve')}>
                              {rowActionLoading === row.rate_id ? '…' : 'Approve'}
                            </button>
                          )}
                          {state === 'pending_approval' && isSelf && (
                            <span className="text-tok-xs text-[rgb(var(--color-text-muted))] italic">
                              Awaiting another approver
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Validation tab ── */}
      {activeTab === 'validate' && (
        <div className="flex flex-col gap-4">
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
            Advisory checks for revenue-type rate configuration completeness.
            No hard blocks are enforced here — the engine enforces approval at run time.
          </p>

          {!selectedComponentId && (
            <div className="rounded-lg bg-[rgb(var(--color-surface-muted))] border border-[rgb(var(--color-border))] px-5 py-8 text-center">
              <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Select a plan component above.</p>
            </div>
          )}

          {selectedComponentId && !validationSummary && (
            <div className="rounded-lg bg-[rgb(var(--color-surface-muted))] border border-[rgb(var(--color-border))] px-5 py-6 text-center">
              <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
                No rate bands configured for this component. Add rate bands in the Rate Definition tab to see validation results.
              </p>
            </div>
          )}

          {selectedComponentId && validationSummary && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div className="rounded-lg bg-[rgb(var(--color-surface-muted))] border border-[rgb(var(--color-border))] px-4 py-3">
                  <p className="text-tok-xs text-[rgb(var(--color-text-muted))] font-medium uppercase tracking-wide">Configured types</p>
                  <p className="mt-1 text-tok-lg font-semibold text-[rgb(var(--color-text))]">{validationSummary.configured}</p>
                </div>
                <div className="rounded-lg bg-[rgb(var(--color-surface-muted))] border border-[rgb(var(--color-border))] px-4 py-3">
                  <p className="text-tok-xs text-[rgb(var(--color-text-muted))] font-medium uppercase tracking-wide">Pending approval</p>
                  <p className={`mt-1 text-tok-lg font-semibold ${unapprovedRows.length > 0 ? 'text-[rgb(var(--color-warning))]' : 'text-[rgb(var(--color-text))]'}`}>
                    {unapprovedRows.length}
                  </p>
                </div>
                <div className={`rounded-lg border px-4 py-3 ${validationSummary.issues.length > 0 ? 'bg-[rgb(var(--color-warning))]/10 border-[rgb(var(--color-warning))]/30' : 'bg-[rgb(var(--color-success))]/10 border-[rgb(var(--color-success))]/30'}`}>
                  <p className="text-tok-xs text-[rgb(var(--color-text-muted))] font-medium uppercase tracking-wide">Issues</p>
                  <p className={`mt-1 text-tok-lg font-semibold ${validationSummary.issues.length > 0 ? 'text-[rgb(var(--color-warning))]' : 'text-[rgb(var(--color-success))]'}`}>
                    {validationSummary.issues.length}
                  </p>
                </div>
              </div>

              {validationSummary.issues.length > 0 && (
                <div className="flex flex-col gap-2">
                  {validationSummary.issues.map((issue, i) => (
                    <div key={i} className="rounded-lg bg-[rgb(var(--color-warning))]/10 border border-[rgb(var(--color-warning))]/30 px-4 py-3 text-tok-sm">
                      <p className="font-medium text-[rgb(var(--color-warning))]">
                        {issue.type === 'unapproved' && `${issue.revType}: ${issue.count} band row${issue.count !== 1 ? 's' : ''} pending approval`}
                        {issue.type === 'gap' && `${issue.revType}: ${issue.detail}`}
                        {issue.type === 'no_quota' && issue.detail}
                      </p>
                      <p className="mt-1 text-[rgb(var(--color-text-muted))]">
                        {issue.type === 'unapproved' && 'Submit and approve these rows before the final run.'}
                        {issue.type === 'gap' && 'Bands must be contiguous — the from_pct of each band must equal the to_pct of the previous band.'}
                        {issue.type === 'no_quota' && 'Add quota mappings in the Quota Mapping tab, or the engine will use proportional allocation.'}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              {validationSummary.issues.length === 0 && (
                <div className="rounded-lg bg-[rgb(var(--color-success))]/10 border border-[rgb(var(--color-success))]/30 px-5 py-4">
                  <p className="text-tok-sm font-medium text-[rgb(var(--color-success))]">
                    No issues found. All configured revenue types have approved, contiguous rate bands with quota mappings.
                  </p>
                </div>
              )}

              {/* Per-type coverage summary */}
              <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] shadow-sm overflow-x-auto">
                <table className="min-w-full text-tok-sm border-collapse">
                  <thead>
                    <tr>
                      <th className={thClass}>Revenue type</th>
                      <th className={thClass}>Bands</th>
                      <th className={thClass}>Approved</th>
                      <th className={thClass}>Quota mapped</th>
                      <th className={thClass}>Ready</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeRevenueTypes.map((rt) => {
                      const rows = grouped[rt.revenue_type_code] || [];
                      if (rows.length === 0) return null;
                      const approved = rows.filter((r) => r.approved_by).length;
                      const quotaMapped = quotaMappings.some((m) => m.revenue_type_code === rt.revenue_type_code);
                      const ready = approved === rows.length && quotaMapped;
                      return (
                        <tr key={rt.revenue_type_code} className="hover:bg-[rgb(var(--color-surface-muted))]">
                          <td className={tdClass + ' font-medium'}>{rt.display_name}</td>
                          <td className={tdClass}>{rows.length}</td>
                          <td className={tdClass}>
                            <span className={approved === rows.length ? 'text-[rgb(var(--color-success))] font-medium' : 'text-[rgb(var(--color-warning))]'}>
                              {approved}/{rows.length}
                            </span>
                          </td>
                          <td className={tdClass}>
                            {quotaMapped
                              ? <span className="text-[rgb(var(--color-success))] font-medium">Yes</span>
                              : <span className="text-[rgb(var(--color-text-muted))] italic">No (proportional default)</span>}
                          </td>
                          <td className={tdClass}>
                            <span className={`inline-block px-2 py-0.5 rounded text-tok-xs font-medium ${
                              ready
                                ? 'bg-[rgb(var(--color-success))]/10 text-[rgb(var(--color-success))]'
                                : 'bg-[rgb(var(--color-warning))]/10 text-[rgb(var(--color-warning))]'
                            }`}>
                              {ready ? 'Ready' : 'Pending'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </section>
  );
}
